import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";
import { RainbowArcs } from "../components/motion/RainbowArcs";
import { LAWS } from "../data/laws";

const Mark = ({ className = "" }) => (
  <svg viewBox="0 0 40 24" className={className} aria-hidden="true">
    {[6, 10, 14, 18].map((r, i) => (
      <path
        key={r}
        d={`M ${20 - r} 22 A ${r} ${r} 0 0 1 ${20 + r} 22`}
        stroke="currentColor"
        strokeWidth="1.4"
        fill="none"
        strokeOpacity={1 - i * 0.2}
      />
    ))}
  </svg>
);

/* Card art is 3.75 × 2.25 in (3.5 × 2 trim + 0.125 in bleed on every side). */
const Face = ({ children, className = "", testId }) => (
  <div
    data-testid={testId}
    className={`card-face relative h-[2.25in] w-[3.75in] overflow-hidden ${className}`}
  >
    {children}
    <div className="pointer-events-none absolute inset-[0.125in] border border-dashed border-current opacity-20 print:hidden" />
  </div>
);

export default function Card() {
  const [params] = useSearchParams();
  const url = params.get("url") || "https://oneprinciple.org";
  const display = url.replace(/^https?:\/\//, "").replace(/\/$/, "");

  return (
    <div data-testid="card-page" className="min-h-screen bg-parchment-deep px-5 py-16 text-ink print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl print:hidden">
        <p className="eyebrow">Print-ready</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl">Business card</h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink-soft">
          Standard 3.5 × 2 in with 0.125 in bleed. The dashed line is the trim guide (it does not
          print). Use “Save as PDF” in the print dialog, at 100% scale with margins set to none.
          The QR code points to <code className="text-xs">{display}</code>.
        </p>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="card-print-button"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-medium text-parchment transition-colors hover:bg-terracotta"
        >
          <Printer size={15} /> Print / Save as PDF
        </button>
      </div>

      <div className="mx-auto mt-12 flex max-w-3xl flex-col items-center gap-10 print:mt-0 print:block">
        {/* FRONT */}
        <Face testId="card-front" className="bg-parchment text-ink shadow-[0_20px_60px_rgba(28,25,23,0.12)] print:shadow-none">
          <div className="absolute inset-[0.125in] flex gap-[0.18in] p-[0.2in]">
            <div className="flex w-[1.05in] shrink-0 flex-col justify-between border-r border-ink/10 pr-[0.16in]">
              <Mark className="h-4 w-7 text-terracotta" />
              <span className="font-serif text-[76pt] leading-[0.8] text-terracotta" aria-hidden="true">7</span>
            </div>
            <div className="flex flex-1 flex-col justify-between">
              <div>
                <p className="font-serif text-[21pt] font-medium leading-none tracking-tight">Seven Mitzvot</p>
                <p className="mt-[0.06in] font-serif text-[10pt] text-ink-muted" dir="rtl" style={{ textAlign: "left" }}>
                  שבע מצוות בני נח
                </p>
              </div>
              <div>
                <p className="font-serif text-[10pt] italic leading-snug text-ink">
                  A clear guide to the Seven Laws of Noah — what the Torah asks of every person,
                  and what it means for daily life.
                </p>
                <p className="mt-[0.1in] font-mono text-[7pt] uppercase tracking-[0.22em] text-terracotta">{display}</p>
              </div>
            </div>
          </div>
        </Face>

        {/* BACK */}
        <Face testId="card-back" className="bg-ink text-parchment shadow-[0_20px_60px_rgba(28,25,23,0.2)] print:shadow-none print:break-before-page">
          <RainbowArcs
            animate={false}
            className="pointer-events-none absolute -bottom-[0.95in] left-1/2 w-[3.9in] -translate-x-1/2 text-terracotta/35"
          />
          <div className="absolute inset-[0.125in] flex gap-[0.2in] p-[0.2in]">
            <div className="flex flex-1 flex-col justify-between">
              <div>
                <p className="font-serif text-[15pt] leading-[1.02] tracking-tight">
                  Seven laws.
                  <br />
                  <span className="italic text-parchment/70">One principle.</span>
                </p>
                <p className="mt-[0.05in] font-serif text-[8pt] italic leading-snug text-terracotta">
                  Loyalty — to oneself, to G-d, and to the world.
                </p>
              </div>
              <ol className="grid grid-cols-1 gap-y-[0.018in]">
                {LAWS.map((l) => (
                  <li key={l.slug} className="flex items-baseline gap-[0.06in] whitespace-nowrap font-mono text-[5pt] uppercase tracking-[0.1em] text-parchment/75">
                    <span className="text-terracotta">0{l.number}</span>
                    {l.number === 6 ? "Flesh from a living animal" : l.title}
                  </li>
                ))}
              </ol>
            </div>
            <div className="flex shrink-0 flex-col items-center justify-center gap-[0.08in]">
              <div className="bg-parchment p-[0.05in]" data-testid="card-qr">
                <QRCodeSVG value={url} size={78} level="M" fgColor="#1C1917" bgColor="#FBF9F5" />
              </div>
              <p className="font-mono text-[6pt] uppercase tracking-[0.2em] text-parchment/70">{display}</p>
            </div>
          </div>
        </Face>
      </div>
    </div>
  );
}
